import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthDistributionService, GrowthPublishJob, GrowthStorefrontApplyRequest } from './growth-distribution.service';
import { GrowthMarketingService } from './growth-marketing.service';
import {
  buildGrowthPublishChannelOptions,
  buildGrowthStorefrontApplyTargetOptions,
  growthPublishChannelLabel
} from './growth-channel.constants';

@Component({
  standalone: true,
  selector: 'ain-growth-publish-operations',
  imports: [
    FormsModule,
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    GrowthContentAssetSelectorComponent,
    OfferingSelectorComponent,
    TableModule,
    ButtonModule,
    SelectModule,
    DialogModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.publishOperations.title')" [description]="t('growth.publishOperations.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.publishOperations.help.title'" [descriptionKey]="'growth.publishOperations.help.description'"
        [bulletKeys]="['growth.publishOperations.help.bullets.sandbox','growth.publishOperations.help.bullets.storefront','growth.publishOperations.help.bullets.approval']" />

      <section class="card toolbar-card">
        <a routerLink="/growth/marketing/article-publishing" pButton [label]="t('nav.growth.articlePublishing')"></a>
        <a routerLink="/growth/marketing/content-assets/new" pButton severity="secondary" [label]="t('growth.articlePublishing.reviewList.actions.newArticle')"></a>
      </section>

      <section class="card">
        <div class="filter-grid">
          <ain-form-field [label]="t('growth.publishOperations.filters.status')">
            <p-select [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="statusFilter" [showClear]="true" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.marketing.calendar.fields.channel')">
            <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="channelFilter" [showClear]="true" appendTo="body" />
          </ain-form-field>
        </div>
        <button pButton type="button" [label]="t('catalog.action.refresh')" (click)="loadJobs()"></button>
      </section>

      <section class="card">
        @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
        @else if (jobs().length === 0) { <ain-state type="empty" [title]="t('growth.publishOperations.empty')" /> }
        @else {
          <p-table [value]="jobs()" [rows]="15" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.marketing.calendar.columns.channel') }}</th>
                <th>{{ t('growth.publishOperations.columns.status') }}</th>
                <th>{{ t('growth.publishOperations.columns.provider') }}</th>
                <th>{{ t('growth.publishOperations.columns.externalRef') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ channelLabel(row.channelKey) }}</td>
                <td>{{ publishJobStatusLabel(row.status) }}</td>
                <td>{{ row.providerMode }}</td>
                <td>{{ row.externalRef || row.lastError || '—' }}</td>
                <td>
                  <a
                    [routerLink]="['/growth/marketing/article-publishing', row.contentAssetId]"
                    pButton
                    size="small"
                    severity="secondary"
                    [label]="t('growth.marketing.calendar.actions.openContent')"></a>
                  @if (isPublishJobFailed(row.status)) {
                    <button pButton size="small" [label]="t('growth.publishOperations.actions.retry')" (click)="retry(row.id)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <section class="card">
        <div class="toolbar">
          <h3>{{ t('growth.publishOperations.storefront.title') }}</h3>
          <button pButton type="button" size="small" [label]="t('growth.publishOperations.storefront.create')" (click)="openCreateDialog()"></button>
          <a routerLink="/marketplace/storefront/publishing" pButton severity="help" size="small" [label]="t('growth.search.seo.actions.openStorefrontPublishing')"></a>
        </div>
        <p-table [value]="storefrontRequests()" [rows]="10" [paginator]="true">
          <ng-template pTemplate="header"><tr><th>{{ t('growth.publishOperations.storefront.target') }}</th><th>{{ t('growth.strategy.columns.status') }}</th><th>{{ t('growth.strategy.columns.integration') }}</th><th></th></tr></ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ channelLabel(row.targetType) }}</td>
              <td>{{ storefrontStatusLabel(row.status) }}</td>
              <td>
                {{ storefrontIntegrationStatusLabel(row) }}
                @if (isStorefrontDeferred(row)) {
                  <small class="deferred-hint">({{ deferredCode(row) }})</small>
                }
                @if (intentFallbackReasonLabel(row)) {
                  <small class="deferred-hint">{{ intentFallbackReasonLabel(row) }}</small>
                }
              </td>
              <td>
                @if (isStorefrontAwaitingApproval(row.status)) {
                  <button pButton size="small" [label]="t('growth.strategy.actions.submit')" (click)="requestStorefrontApproval(row.id)"></button>
                }
                @if (isStorefrontPendingApproval(row)) {
                  @if (hasIntentDeepLink(row)) {
                    <a
                      [routerLink]="['/workflow/intents', row.workflowIntentRequestId!]"
                      pButton
                      size="small"
                      severity="secondary"
                      [label]="t('growth.strategy.activation.actions.openIntent')"></a>
                  } @else {
                    <a
                      routerLink="/workflow/intents/approvals/inbox"
                      pButton
                      size="small"
                      severity="secondary"
                      [label]="t('growth.strategy.activation.actions.openApprovalInbox')"></a>
                  }
                }
                @if (isStorefrontApproved(row.status)) {
                  <button pButton size="small" severity="secondary" [label]="t('growth.publishOperations.storefront.apply')" (click)="applyStorefront(row.id)"></button>
                }
                @if (isStorefrontAwaitingApproval(row.status) || isStorefrontDeferred(row)) {
                  <a
                    routerLink="/workflow/intents/governance"
                    pButton
                    size="small"
                    severity="warn"
                    [label]="t('growth.strategy.activation.actions.openGovernance')"></a>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </section>

      <p-dialog
        [header]="t('growth.publishOperations.storefront.dialog.title')"
        [visible]="createDialogVisible()"
        (visibleChange)="createDialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(720px, 95vw)' }">
        <ain-info-hint
          [titleKey]="'growth.search.seo.help.storefrontBoundary.title'"
          [descriptionKey]="'growth.search.seo.help.storefrontBoundary.description'" />
        <div class="dialog-grid">
          <ain-form-field [label]="t('growth.marketing.calendar.columns.contentAsset')">
            <ain-growth-content-asset-selector [(ngModel)]="createModel.contentAssetId" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.publishOperations.storefront.fields.targetType')">
            <p-select [options]="targetTypeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.targetType" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.publishOperations.storefront.fields.offering')">
            <ain-offering-selector [(ngModel)]="createModel.offeringRefId" />
          </ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('growth.publishOperations.storefront.create')" [disabled]="!canCreateStorefrontApply()" (click)="createStorefrontApply()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; margin-top:12px; }
    .toolbar-card { display:flex; gap:8px; flex-wrap:wrap; }
    .filter-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:10px; margin-bottom:8px; }
    .toolbar { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-bottom:8px; }
    .toolbar h3 { margin:0; flex:1 1 auto; }
    .dialog-grid { display:grid; gap:12px; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); }
    .deferred-hint { display:block; color:#6b7280; font-size:0.8rem; }
  `]
})
export class GrowthPublishOperationsComponent implements OnInit {
  private readonly distribution = inject(GrowthDistributionService);
  private readonly marketing = inject(GrowthMarketingService);
  private readonly i18n = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  readonly jobs = signal<GrowthPublishJob[]>([]);
  readonly storefrontRequests = signal<GrowthStorefrontApplyRequest[]>([]);
  readonly loading = signal(false);
  readonly createDialogVisible = signal(false);
  statusFilter: string | null = null;
  channelFilter: string | null = null;
  contentFilter: string | null = null;
  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthPublishChannelOptions(key => this.t(key));
  }
  targetTypeOptions(): Array<{ label: string; value: string }> {
    return buildGrowthStorefrontApplyTargetOptions(key => this.t(key));
  }
  createModel = {
    contentAssetId: null as string | null,
    targetType: 'storefront-pdp',
    offeringRefId: null as string | null
  };
  statusOptions = [
    { label: 'Pending', value: 'Pending' },
    { label: 'Running', value: 'Running' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Failed', value: 'Failed' }
  ];
  t(key: string): string { return this.i18n.translate(key); }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const channel = params.get('channel');
      if (channel) {
        this.channelFilter = channel;
      }
      this.contentFilter = params.get('contentId') || params.get('contentAssetId');
      this.loadJobs();
      this.loadStorefront();
    });
  }

  channelLabel(channelKey: string): string {
    return growthPublishChannelLabel(channelKey, key => this.t(key));
  }

  loadJobs(): void {
    this.loading.set(true);
    this.distribution.listPublishJobs(this.statusFilter ?? undefined, this.channelFilter ?? undefined).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: items => {
        const contentId = this.contentFilter;
        this.jobs.set(contentId ? items.filter(item => item.contentAssetId === contentId) : items);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  loadStorefront(): void {
    this.distribution.listStorefrontApplyRequests().subscribe(items => this.storefrontRequests.set(items));
  }

  openCreateDialog(): void {
    this.createModel = { contentAssetId: null, targetType: 'storefront-pdp', offeringRefId: null };
    this.createDialogVisible.set(true);
  }

  canCreateStorefrontApply(): boolean {
    return Boolean(this.createModel.contentAssetId && this.createModel.offeringRefId && this.createModel.targetType);
  }

  createStorefrontApply(): void {
    if (!this.canCreateStorefrontApply()) return;
    const contentAssetId = this.createModel.contentAssetId!;
    this.marketing.ensureContentReadyForStorefrontApply(contentAssetId).subscribe({
      next: asset => {
        this.distribution.createStorefrontApplyRequest({
          contentAssetId: asset.id,
          targetType: this.createModel.targetType,
          offeringRefId: this.createModel.offeringRefId,
          seoProfileId: null,
          requestPayloadJson: '{}',
          idempotencyKey: `sf-apply-${Date.now()}`,
          correlationId: crypto.randomUUID()
        }).subscribe({
          next: () => {
            this.createDialogVisible.set(false);
            this.loadStorefront();
          }
        });
      }
    });
  }

  retry(jobId: string): void {
    this.distribution.retryPublishJob(jobId, `retry-${jobId}-${Date.now()}`).subscribe(() => this.loadJobs());
  }

  requestStorefrontApproval(id: string): void {
    this.distribution.requestStorefrontApplyApproval(id).subscribe({
      next: () => this.loadStorefront(),
      error: () => this.loadStorefront()
    });
  }

  applyStorefront(id: string): void {
    this.distribution.applyStorefrontRequest(id).subscribe(() => this.loadStorefront());
  }

  publishJobStatusLabel(status: string | number): string {
    if (status === 0 || status === 'Pending') return this.t('growth.publishOperations.status.pending');
    if (status === 1 || status === 'Running') return this.t('growth.publishOperations.status.running');
    if (status === 2 || status === 'Completed') return this.t('growth.publishOperations.status.completed');
    if (status === 3 || status === 'Failed') return this.t('growth.publishOperations.status.failed');
    return String(status);
  }

  isPublishJobFailed(status: string | number): boolean {
    return status === 'Failed' || status === 3;
  }

  storefrontStatusLabel(status: string | number): string {
    if (status === 0 || status === 'Draft') return this.t('growth.strategy.activation.status.draft');
    if (status === 1 || status === 'PendingApproval') return this.t('growth.strategy.activation.status.pending');
    if (status === 2 || status === 'Approved') return this.t('growth.strategy.activation.status.approved');
    if (status === 3 || status === 'Applied') return this.t('growth.publishOperations.storefront.applied');
    return String(status);
  }

  isStorefrontAwaitingApproval(status: string | number): boolean {
    return status === 'Draft' || status === 0 || status === 'PendingApproval' || status === 1;
  }

  isStorefrontApproved(status: string | number): boolean {
    return status === 'Approved' || status === 2;
  }

  isStorefrontPendingApproval(row: GrowthStorefrontApplyRequest): boolean {
    const pending = row.status === 'PendingApproval' || Number(row.status) === 1;
    return pending && !!row.workflowProcessInstanceId && !this.isStorefrontDeferred(row);
  }

  isStorefrontDeferred(row: GrowthStorefrontApplyRequest): boolean {
    return this.isStorefrontAwaitingApproval(row.status)
      && Boolean(row.lastWorkflowDeferredCode);
  }

  deferredCode(row: GrowthStorefrontApplyRequest): string {
    return row.lastWorkflowDeferredCode ?? '';
  }

  hasIntentDeepLink(row: GrowthStorefrontApplyRequest): boolean {
    return typeof row.workflowIntentRequestId === 'string' && row.workflowIntentRequestId.length > 0;
  }

  intentFallbackReasonLabel(row: GrowthStorefrontApplyRequest): string {
    const reason = row.workflowIntentRouteFallbackReason?.trim();
    if (!reason) {
      return '';
    }

    const key = `growth.strategy.activation.intentFallback.${reason}`;
    const translated = this.t(key);
    return translated === key
      ? this.t('growth.strategy.activation.intentFallback.default')
      : translated;
  }

  storefrontIntegrationStatusLabel(row: GrowthStorefrontApplyRequest): string {
    if (this.isStorefrontDeferred(row)) return this.t('growth.strategy.activation.integration.deferred');
    if (this.isStorefrontPendingApproval(row)) return this.t('growth.strategy.activation.integration.pendingApproval');
    if (this.isStorefrontApproved(row.status)) return this.t('growth.strategy.activation.integration.executed');
    return this.t('growth.strategy.activation.integration.planned');
  }
}
